import {
  KeycloakConfig,
  KeycloakInitOptions,
  KeycloakLoginOptions,
  KeycloakLogoutOptions
} from 'keycloak-js';

import Keycloak from 'keycloak-js';

import {env} from '@/config';

import {ConsoleLogger} from './logger';

import {
  loginUser,
  purgeAuth,
  setCompanySportsLoaded,
  setCurrentCompanyId,
  setIsUserHealthManager,
  setKeycloakReady,
  setPaymentFeeData,
  setUserBlacklist,
  updateToken
} from '@/redux-store/slices/auth';
import {
  purgeUserInfos,
  setUserInfos,
  setUserInfosFormData
} from '@/redux-store/slices/user-infos';

import {resetEventFormData} from '@/redux-store/slices/events/';
import {resetGroupFormData} from '@/redux-store/slices/groups';

import {setGroupsInRedux} from '@/redux-store/slices/groups';

import {persistor, store} from '@/redux-store/store';
import {fetchUserData} from '@/rest/user/fetch-user-data';
import {removeAllGroups} from '@/redux-store/slices/groups';

import {setEventsInRedux, removeAllEvents} from '@/redux-store/slices/events';
import {setFormModules, setModules} from '@/redux-store/slices/modules';
import {Module} from '@/types/module';
import {findMyCompanyGroups} from '@/rest/groups/find-my-company-groups';
import {findMyCompanyEvents} from '@/rest/events/find-my-company-events';
import {getCompanyInfos} from '@/rest/company/get-company-infos';
import {
  resetCompanyData,
  setCompanyData,
  setCompanyFormData,
  setEmployeesInRedux
} from '@/redux-store/slices/company';
import {setRefreshPromise} from './axios';
import {getAllEmployees} from '@/rest/employees/get-all-employees';
import {fetchCompanies} from '@/rest/admin/companies/fetch-companies';
import {setCompaniesInRedux} from '@/redux-store/slices/admin/companies';
import {fetchModules} from '@/rest/admin/companies/fetch-modules';
import {LogLevel} from '@/constants';
import {
  purgeRoomJoinedData,
  roomDataPersistConfig
} from '@/redux-store/slices/matrix/room-data';
import {findMyGroups} from '@/rest/groups/find-my-groups';
import {checkVerification} from './payments';
import {filterPersistConfig, resetFilter} from '@/redux-store/slices/filter';
import {getBillingData} from '@/rest/payments/get-billing-data';
import {resetBillingData} from '@/redux-store/slices/billing-data';
import {findTrainerEvents} from '@/rest/events/find-trainer-events';
import {findTrainerGroups} from '@/rest/groups/find-trainer-groups';
import {getPaymentFee} from '@/rest/payments/get-payment-fee';
import {getCompanyTax} from '@/rest/payments/get-company-tax';
import {purgeStoredState} from 'redux-persist';
import {Role} from '@/types/common';

const {IAM_HOST, IAM_REALM, IAM_CLIENT, HOST, PROTOCOL, APP_VARIANT} = env;

const logger = new ConsoleLogger(LogLevel.INFO);

const keycloakConfiguration: KeycloakConfig = {
  url: `${IAM_HOST}/auth`,
  realm: IAM_REALM,
  clientId: IAM_CLIENT
};

const KEYCLOAK_INIT_CONFIG: KeycloakInitOptions = {
  onLoad: 'check-sso',
  silentCheckSsoRedirectUri: `${PROTOCOL}://${HOST}/auth/initredirect`,
  checkLoginIframe: false
};

const KEYCLOAK_LOGOUT_HIDDEN: KeycloakLogoutOptions = {
  redirectUri: `${PROTOCOL}://${HOST}/auth/redirect`
};

export interface UserInfo {
  token: string;
  email: string;
  firstName: string;
  lastName: string;
  userName: string;
  userRole: string;
}

const defaultUser: UserInfo = {
  token: 'undefined',
  firstName: 'John',
  lastName: 'Doe',
  userName: 'john.doe',
  email: 'info@example.com',
  userRole: 'none'
};

export const getShortToken = (token: string): string => {
  return `${token.substring(0, 6)}...${token.substring(token.length - 6)}`;
};

//const WHITELIST = ['/auth/redirect', '/'];
export class KeycloakService {
  private _keycloakInstance: Keycloak | undefined;
  private _authenticated: boolean | null = null;
  private _initialized = false;

  get keycloakInstance(): Keycloak {
    if (!this._keycloakInstance) {
      this._keycloakInstance = new Keycloak(keycloakConfiguration);
      this._keycloakInstance.onAuthSuccess = () => {
        logger.info('[keycloak]onAuthSuccess');
        this.setAuthenticated(true);
      };
      this._keycloakInstance.onAuthLogout = () => {
        logger.info('[keycloak]onAuthLogout');
        this.setAuthenticated(false);
      };
      this._keycloakInstance.onReady = (authenticated) => {
        logger.info(`[keycloak]onReady, authenticated: ${authenticated}`);
        this._initialized = true;
        if (authenticated) {
          this.setAuthenticated(true);
        } else {
          const storedToken = store.getState().auth.keycloakUser?.token;
          console.log('else, stored token ....: ', storedToken);
          if (storedToken) {
            console.log('wanna set to false');
            this.setAuthenticated(false);
          } else {
            store.dispatch(setKeycloakReady(true));
          }
        }
      };

      this._keycloakInstance.init(KEYCLOAK_INIT_CONFIG);
    }
    return this._keycloakInstance;
  }
  get authenticated(): boolean {
    return this._authenticated || false;
  }
  get initialized(): boolean {
    return this._initialized;
  }
  get userInfo(): UserInfo {
    if (this.keycloakInstance && this.keycloakInstance.tokenParsed) {
      const {token, tokenParsed} = this.keycloakInstance;
      const {email, given_name, family_name, preferred_username, sportjoe} =
        tokenParsed;

      const {user_role} = sportjoe || {};

      const adjustedUserRole = this.adjustUserRole(user_role);

      return {
        token: token as string,
        email: email || '',
        firstName: given_name || '',
        lastName: family_name || '',
        userName: preferred_username || '',
        userRole: adjustedUserRole
      };
    }
    return defaultUser;
  }

  public onErrorCallback: ((error: Error) => void) | null = null;

  public resetAuthData() {
    persistor.pause();

    Promise.all([
      purgeStoredState(roomDataPersistConfig),
      purgeStoredState(filterPersistConfig)
    ])
      .then(() => {
        store.dispatch(purgeAuth());
        store.dispatch(purgeUserInfos());
        store.dispatch(resetEventFormData());
        store.dispatch(resetGroupFormData());
        store.dispatch(removeAllGroups());
        store.dispatch(removeAllEvents());
        store.dispatch(purgeRoomJoinedData());
        store.dispatch(resetFilter({type: 'event', preserveLocation: true}));
        store.dispatch(resetFilter({type: 'group', preserveLocation: true}));
        store.dispatch(resetBillingData());
        store.dispatch(resetCompanyData());
      })
      .catch((err) => {
        console.error('error purging persistor', err);
      });
  }

  private onKeycloakLoaded = () => {
    if (typeof document !== 'undefined') {
      const scriptTag = document.getElementById('keycloakscriptload');
      if (scriptTag) {
        scriptTag.removeEventListener('load', this.onKeycloakLoaded);
        logger.info('Async load of keycloak.js finished');
        if (this.keycloakInstance) {
          logger.info('Keycloak instance present after async load of sources');
        }
      } else {
        logger.warn('scripttag for loading keycloak.js not found');
      }
    }
  };

  private setAuthenticated = (authenticated: boolean) => {
    console.log('called set authenticated with parameter: ', authenticated);
    // console.log({
    //   authenticated: {
    //     prev: this._authenticated,
    //     next: authenticated
    //   }
    // });
    if (authenticated !== this._authenticated) {
      this._authenticated = authenticated;

      if (authenticated) {
        setRefreshPromise(this.refreshToken);
        store.dispatch(loginUser(this.userInfo));

        fetchUserData()
          .then((userInfos) => {
            if (userInfos) {
              if (
                userInfos.companies &&
                userInfos.companies.length > 0 &&
                this.userInfo.userRole === 'TRAINER'
              ) {
                const firstCompanyId = userInfos.companies[0]._id;
                store.dispatch(setCurrentCompanyId(firstCompanyId));
              }
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const {picture: _, ...restOfuserInfos} = userInfos;
              if (
                APP_VARIANT === 'sjone' &&
                (!userInfos.companies ||
                  userInfos.companies.length === 0 ||
                  !userInfos.companies[0].confirmed)
              ) {
                if (this.onErrorCallback) {
                  this.onErrorCallback(new Error('Insufficient rights'));
                }
                this.logoutExternal();
                this.resetAuthData();
                return;
              }
              store.dispatch(setUserInfosFormData(restOfuserInfos));
              store.dispatch(setUserInfos(userInfos));

              if (userInfos.picture) {
                store.dispatch(setUserInfosFormData({avatarDeleted: false}));
                store.dispatch(setUserInfos({avatarDeleted: false}));
                store.dispatch(setUserBlacklist(userInfos.userBlacklist));
              }

              if (
                APP_VARIANT === 'sjone' &&
                userInfos.companies &&
                userInfos.companies.length === 1
              ) {
                const companyRole = userInfos.companies[0].role;
                if (companyRole === 'HEALTH_MANAGER') {
                  store.dispatch(setIsUserHealthManager());
                  getCompanyInfos()
                    .then((companyInfos) => {
                      if (companyInfos) {
                        if (!companyInfos.picture) {
                          companyInfos.avatarDeleted = true;
                        }

                        store.dispatch(setCompanyFormData(companyInfos));
                        store.dispatch(setCompanyData(companyInfos));
                        if (
                          !companyInfos.allowedSports ||
                          companyInfos.allowedSports.length === 0
                        ) {
                          store.dispatch(setCompanySportsLoaded(true));
                        }
                      }
                    })
                    .catch((error) => {
                      console.error('Error getting company infos: ', error);
                    });
                }
              }

              const modules = userInfos.companies.flatMap(
                (company: {id: {modules: Module[]}}) => company.id.modules
              );
              if (modules && modules.length > 0) {
                store.dispatch(setModules(modules));
              }

              if (
                APP_VARIANT === 'sjone' &&
                userInfos.companies &&
                userInfos.companies.length === 1 &&
                userInfos.companies[0].role === 'HEALTH_MANAGER'
              ) {
                findMyCompanyEvents()
                  .then((events) => {
                    store.dispatch(setEventsInRedux(events));
                  })
                  .catch((error) => {
                    console.error('Error fetching my events:', error);
                  });

                findMyCompanyGroups()
                  .then((groups) => {
                    store.dispatch(setGroupsInRedux(groups));
                  })
                  .catch((error) => {
                    console.error('Error fetching groups: ', error);
                  });

                getAllEmployees()
                  .then((employees) => {
                    store.dispatch(setEmployeesInRedux(employees));
                  })
                  .catch((error) => {
                    console.error('Error fetching employees: ', error);
                  });
              } else if (
                APP_VARIANT === 'sjone' &&
                userInfos.companies &&
                userInfos.companies.length > 0 &&
                this.userInfo.userRole === 'TRAINER'
              ) {
                findTrainerEvents()
                  .then((events) => {
                    store.dispatch(setEventsInRedux(events));
                  })
                  .catch((error) => {
                    console.error('Error fetching trainer events:', error);
                  });
                findTrainerGroups()
                  .then((groups) => {
                    store.dispatch(setGroupsInRedux(groups));
                  })
                  .catch((error) => {
                    console.error('Error fetching groups: ', error);
                  });
                store.dispatch(setCompanySportsLoaded(true));
              } else {
                findMyGroups()
                  .then((groups) => {
                    store.dispatch(setGroupsInRedux(groups));
                  })
                  .catch((error) => {
                    console.error('Error fetching groups: ', error);
                  });
                if (
                  !userInfos.companies[0].id.allowedSports ||
                  userInfos.companies[0].id.allowedSports.length === 0
                ) {
                  store.dispatch(setCompanySportsLoaded(true));
                }
              }
              if (this.userInfo.userRole === 'ADMIN') {
                fetchCompanies()
                  .then((companies) => {
                    store.dispatch(setCompaniesInRedux(companies));
                  })
                  .catch((error) => {
                    console.error('Error fetching companies: ', error);
                  });
                fetchModules()
                  .then((fetchedModules) => {
                    store.dispatch(setFormModules(fetchedModules));
                  })
                  .catch((error) => {
                    console.error('Error fetching form modules: ', error);
                  });
              }
              checkVerification().catch((error) => {
                console.error('Error fetching verification: ', error);
              });

              checkVerification()
                .then((isVerified) => {
                  if (isVerified && userInfos.odooCompany) {
                    return getCompanyTax(userInfos.odooCompany).catch((error) =>
                      console.error('Error fetching company tax: ', error)
                    );
                  }
                })
                .catch((error) => {
                  console.error('Error fetching verification: ', error);
                });

              getPaymentFee()
                .then((paymentFeeData) => {
                  if (paymentFeeData) {
                    store.dispatch(setPaymentFeeData(paymentFeeData));
                  }
                })
                .catch((error) => {
                  console.error('Error fetching payment fee data: ', error);
                });

              getBillingData().catch((error) => {
                console.warn('Error fetching billing data: ', error);
              });
            } else {
              console.error('No user data received from server.');
              throw new Error('No user data received from server.');
            }
          })
          .catch((error) => {
            console.error('Error fetching user data:', error);
          })
          .finally(() => store.dispatch(setKeycloakReady(true)));
      } else {
        console.log('delete keycloakuser in store');
        setRefreshPromise(null);
        this.resetAuthData();
        store.dispatch(setKeycloakReady(true));
      }
    }
  };

  private init = (instance: Keycloak): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      instance
        .init(KEYCLOAK_INIT_CONFIG)
        .then((authenticated) => {
          logger.info('Keycloak initialized', {authenticated});
          this.setAuthenticated(authenticated);
          this._initialized = true;
          resolve(authenticated);
        })
        .catch((err) => {
          logger.error('Keycloak initialization failed', err);
          reject(err);
        });
    });
  };

  public handleKeycloakLinkExternal = (url: string): Promise<void> => {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('Window in not available (SSR issue)'));
    }
    if (this.keycloakInstance && typeof window !== 'undefined') {
      return new Promise<void>((resolve, reject) => {
        try {
          logger.info('Redirecting to Keycloak login URL:', url);
          window.location.href = url;
          resolve();
        } catch (e) {
          reject(e);
        }
      });
    }
    return Promise.reject(new Error('Keycloak instance missing'));
  };

  private handleKeycloakLinkIframe = (url: string): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const iframe = document.getElementById('keycloak-link-handler');
      if (iframe) {
        (iframe as HTMLIFrameElement).src = url;
        const callback = (event: MessageEvent): void => {
          try {
            if (event.origin !== `${PROTOCOL}://${HOST}`) {
              return;
            }
            window.removeEventListener('message', callback);
            logger.info(
              'linkIframe: removedEventListener - data from iframe: ',
              event.data
            );
            resolve();
          } catch (e) {
            reject(e);
          }
        };
        window.addEventListener('message', callback, false);
        logger.info('linkIframe: registeredEventListener');
      } else {
        // iframe not existing yet!?!
      }
    });
  };

  public getToken(): string | undefined {
    if (this.keycloakInstance && this.keycloakInstance.token) {
      return this.keycloakInstance.token;
    }
    return undefined;
  }

  public loginUrl = (): string => {
    if (this.keycloakInstance) {
      const currentUrl = window.location.href;
      let destUrl: string;

      if (currentUrl.includes('/group/') || currentUrl.includes('/event/')) {
        destUrl = currentUrl;
      } else {
        const {
          appTag,
          currentEvent,
          currentOnDemandEvent,
          currentGroup,
          currentTab
        } = store.getState().auth;
        const {modules} = store.getState().modules;
        const route = appTag || 'home';
        let documentType: string | undefined;

        if (APP_VARIANT === 'sjone') {
          const companyHasModules = modules.length;
          if (companyHasModules ? currentTab === 2 : currentTab === 1) {
            documentType = 'event';
          } else if (companyHasModules ? currentTab === 3 : currentTab === 2) {
            documentType = 'group';
          }
        } else {
          if (currentTab === 0) {
            documentType = 'event';
          } else if (currentTab === 1) {
            documentType = 'group';
          } else if (currentTab === 2) {
            documentType = 'onDemandEvent';
          }
        }

        console.log('document type: ', documentType);

        let suffix: string;
        if (documentType) {
          if (documentType === 'event' && currentEvent) {
            suffix = `event/${currentEvent}`;
            if (currentGroup) {
              suffix += `/${currentGroup}`;
            }
          } else if (documentType === 'group' && currentGroup) {
            suffix = `group/${currentGroup}`;
            if (currentEvent) {
              suffix += `/${currentEvent}`;
            }
          } else if (documentType === 'onDemandEvent' && currentOnDemandEvent) {
            suffix = `on-demand/${currentOnDemandEvent}`;
          } else {
            suffix = route;
          }
        } else {
          suffix = route;
        }
        destUrl = `${PROTOCOL}://${HOST}/${suffix}`;
      }

      const url: KeycloakLoginOptions = {
        redirectUri: destUrl
      };
      return `${this.keycloakInstance.createLoginUrl(url)}`;
      // return `${this.keycloakInstance.createLoginUrl(KEYCLOAK_LOGIN_EXTERNAL)}`;
    }
    return '';
  };

  public loginExternal = (): Promise<boolean> => {
    return new Promise<boolean>((resolve, reject) => {
      const loginUrl = this.loginUrl();
      logger.info('loginUrl', loginUrl);
      this.handleKeycloakLinkExternal(loginUrl)
        .then(() => {
          if (this.keycloakInstance) {
            this.init(this.keycloakInstance).then(resolve).catch(reject);
          }
          return;
        })
        .catch(reject);
    });
  };

  public logoutExternal = (): Promise<boolean> => {
    return new Promise<boolean>((resolve, reject) => {
      const logoutUrl = this.keycloakInstance?.createLogoutUrl(
        KEYCLOAK_LOGOUT_HIDDEN
      );
      if (!logoutUrl) {
        return reject(new Error('Could not create logout url'));
      }
      logger.info('logoutUrl', logoutUrl);
      this.handleKeycloakLinkIframe(logoutUrl)
        .then(() => {
          logger.info('Logout external returned');
          if (this.keycloakInstance) {
            // unregisterServiceWorkers();
            this.keycloakInstance.clearToken();
            this.setAuthenticated(false);
            resolve(true);
          } else {
            reject(new Error('Keycloak instance not initialized'));
          }
        })
        .catch(reject);
    });
  };

  private refreshToken = async (): Promise<string> => {
    const refreshed = await this.keycloakInstance.updateToken(5);
    if (refreshed) {
      logger.info('Token refreshed');
    } else {
      logger.warn('Token not refreshed, still valid');
    }
    if (this.keycloakInstance.token) {
      store.dispatch(updateToken(this.keycloakInstance.token));
      return this.keycloakInstance.token;
    }
    throw new Error('No token available in keycloakInstance');
  };

  private adjustUserRole = (role?: Role): Role => {
    const clubRoles = [
      Role.CLUB_USER,
      Role.CLUB_MEMBER,
      Role.CLUB_ADMIN,
      Role.CLUB_INSTITUTE,
      Role.CLUB_TRAINER
    ];
    if (role && clubRoles.includes(role)) {
      return Role.USER;
    }
    return (role as Role) || Role.USER;
  };
}

// singleton
const instance = new KeycloakService();
export default instance;
